// CustomFieldsPage — tabbed admin page for conversation custom fields.
//
// Three tabs:
//   - Definitions: CRUD on field definitions (the 9 fixed field keys).
//   - Values: hierarchical 4-level value tree per selected definition.
//   - Dependencies: cross-field UI filtering rules.
//
// Plan revisions applied:
//   §0.C — Definitions table has a "Required on Close" column + drawer toggle.
//   §0.D — Values are fetched via raw `api.get` (useAdminResource mishandles
//          query-string paths). CRUD on values is manual api.post/patch/delete
//          followed by a manual refetch.
//   §0.E — Hard-deletes go through `confirmDialog`.
//   §0.F — The "+ New" definition drawer's Field Key dropdown only offers the
//          unused subset of the 9 fixed keys.

import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { z } from 'zod'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import { ZodForm, FormText, FormDropdown, FormSwitch, FormNumber } from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useToast } from '../../../hooks/useToast.js'
import { api } from '../../../api/client.js'
import CustomFieldValuesTree, {
  assembleTree,
} from '../../../components/admin/conversation-engine/CustomFieldValuesTree.js'
import CustomFieldValuesEditor, {
  type ValueInput,
} from '../../../components/admin/conversation-engine/CustomFieldValuesEditor.js'
import CustomFieldDependenciesEditor, {
  type Dependency,
  type FieldValue,
} from '../../../components/admin/conversation-engine/CustomFieldDependenciesEditor.js'
import Select from '../../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Types — derived from the admin route files
// ---------------------------------------------------------------------------

export interface FieldDefinition {
  id: string
  fieldKey: string
  label: string
  isRequired: boolean
  requiredOnClose: boolean
  allowMultiple: boolean
  sortOrder: number
}

interface ValueRow {
  id: string
  fieldDefinitionId: string
  parentValueId: string | null
  level: number
  label: string
  code: string
  color?: string | null
  icon?: string | null
  isActive?: boolean
  sortOrder?: number
  slaOverrideId?: string | null
}

interface SlaPolicy {
  id: string
  name: string
}

// The 9 fixed field keys — matches FieldKeyEnum in
// apps/api/src/routes/conversations/admin/custom-field-definitions.ts
const FIELD_KEYS = [
  'status',
  'category',
  'subcategory',
  'type',
  'subtype',
  'impact',
  'priority',
  'urgency',
  'complexity',
] as const

const TABS = ['definitions', 'values', 'dependencies'] as const
type Tab = (typeof TABS)[number]

const TAB_LABELS: Record<Tab, string> = {
  definitions: 'Definitions',
  values: 'Values',
  dependencies: 'Dependencies',
}

const DEFINITIONS_PATH = '/admin/conversations/custom-fields/definitions'
const VALUES_PATH = '/admin/conversations/custom-fields/values'
const DEPENDENCIES_PATH = '/admin/conversations/custom-fields/dependencies'

/** Sentence-case a field key, e.g. `subcategory` -> `Subcategory`. */
function sentenceCase(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1)
}

/**
 * Collect the transitive descendant ids of `rootId` from a flat list of values
 * linked by `parentValueId`. Used to prevent a cycle when editing a value: a
 * value must not be re-parented under one of its own descendants.
 */
export function collectDescendantIds(
  flatValues: Array<{ id: string; parentValueId: string | null }>,
  rootId: string,
): Set<string> {
  const descendants = new Set<string>()
  const queue: string[] = [rootId]
  while (queue.length > 0) {
    const currentId = queue.shift()!
    for (const v of flatValues) {
      if (v.parentValueId === currentId && !descendants.has(v.id)) {
        descendants.add(v.id)
        queue.push(v.id)
      }
    }
  }
  return descendants
}

// ---------------------------------------------------------------------------
// Definition drawer schema
// ---------------------------------------------------------------------------

const DefinitionSchema = z.object({
  fieldKey: z.string().min(1, 'Field key is required'),
  label: z.string().min(1, 'Label is required'),
  isRequired: z.boolean(),
  requiredOnClose: z.boolean(),
  allowMultiple: z.boolean(),
  sortOrder: z.number().int().min(0),
})

type DefinitionFormValues = z.infer<typeof DefinitionSchema>

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CustomFieldsPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<Tab>('definitions')

  const {
    items: definitions,
    loading: definitionsLoading,
    create: createDefinition,
    update: updateDefinition,
    remove: removeDefinition,
  } = useAdminResource<FieldDefinition>(DEFINITIONS_PATH, { responseKey: 'rows' })

  const {
    items: dependencies,
    refetch: refetchDependencies,
  } = useAdminResource<Dependency>(DEPENDENCIES_PATH, { responseKey: 'rows' })

  const { items: slaPolicies } = useAdminResource<SlaPolicy>('/admin/conversations/sla-policies', {
    responseKey: 'rows',
  })

  return (
    <div className="p-4">
      <PageHeader
        title="Custom Fields"
        description="Define conversation custom fields, their hierarchical values, and cross-field dependencies."
      />

      {/* Tab navigation */}
      <div role="tablist" aria-label="Custom fields sections" className="mb-4 flex gap-1 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab}
            role="tab"
            type="button"
            aria-selected={activeTab === tab}
            className={
              activeTab === tab
                ? '-mb-px border-b-2 border-blue-600 px-4 py-2 text-sm font-medium text-blue-700'
                : '-mb-px border-b-2 border-transparent px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700'
            }
            onClick={() => setActiveTab(tab)}
          >
            {TAB_LABELS[tab]}
          </button>
        ))}
      </div>

      {activeTab === 'definitions' && (
        <DefinitionsTab
          definitions={definitions}
          loading={definitionsLoading}
          create={createDefinition}
          update={updateDefinition}
          remove={removeDefinition}
        />
      )}

      {activeTab === 'values' && (
        <ValuesTab definitions={definitions} slaPolicies={slaPolicies} />
      )}

      {activeTab === 'dependencies' && (
        <DependenciesTab
          definitions={definitions}
          dependencies={dependencies}
          refetchDependencies={refetchDependencies}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Definitions tab
// ---------------------------------------------------------------------------

interface DefinitionsTabProps {
  definitions: FieldDefinition[]
  loading: boolean
  create: (data: unknown) => Promise<FieldDefinition>
  update: (id: string, data: unknown) => Promise<FieldDefinition>
  remove: (id: string) => Promise<void>
}

function DefinitionsTab({
  definitions,
  loading,
  create,
  update,
  remove,
}: DefinitionsTabProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<FieldDefinition | null>(null)
  const formRef = useRef<HTMLFormElement>(null)

  // §0.F — keys not yet configured, offered in the "+ New" dropdown.
  const usedKeys = new Set(definitions.map((d) => d.fieldKey))
  const availableKeys = FIELD_KEYS.filter((k) => !usedKeys.has(k))

  function openCreate(): void {
    setEditing(null)
    setDrawerOpen(true)
  }

  function openEdit(row: FieldDefinition): void {
    setEditing(row)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
  }

  async function handleSubmit(data: DefinitionFormValues): Promise<void> {
    const payload = {
      fieldKey: data.fieldKey,
      label: data.label.trim(),
      isRequired: data.isRequired,
      requiredOnClose: data.requiredOnClose,
      allowMultiple: data.allowMultiple,
      sortOrder: data.sortOrder,
    }
    if (editing) {
      await update(editing.id, payload)
    } else {
      await create(payload)
    }
    closeDrawer()
  }

  function handleDelete(row: FieldDefinition): void {
    confirmDialog({
      message: `Permanently delete the "${row.label}" field? Its values and dependent rules will be orphaned. This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<FieldDefinition>[] = [
    { label: 'Field Key', accessor: 'fieldKey' },
    { label: 'Label', accessor: 'label' },
    { label: 'Required', accessor: (r) => (r.isRequired ? '✓' : '—') },
    { label: 'Required on Close', accessor: (r) => (r.requiredOnClose ? '✓' : '—') },
    { label: 'Allow Multiple', accessor: (r) => (r.allowMultiple ? '✓' : '—') },
    { label: 'Sort', accessor: 'sortOrder' },
  ]

  // For a new definition the field-key default is the first available key.
  const firstAvailable = availableKeys[0] ?? ''

  const defaultValues: DefinitionFormValues = editing
    ? {
        fieldKey: editing.fieldKey,
        label: editing.label,
        isRequired: editing.isRequired,
        requiredOnClose: editing.requiredOnClose,
        allowMultiple: editing.allowMultiple,
        sortOrder: editing.sortOrder,
      }
    : {
        fieldKey: firstAvailable,
        label: firstAvailable ? sentenceCase(firstAvailable) : '',
        isRequired: false,
        requiredOnClose: false,
        allowMultiple: false,
        sortOrder: 0,
      }

  const keyOptions = availableKeys.map((k) => ({ label: sentenceCase(k), value: k }))

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button label="+ New Field" onClick={openCreate} disabled={availableKeys.length === 0} />
      </div>

      <ResourceTable<FieldDefinition>
        columns={columns}
        rows={definitions}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        empty={{
          title: 'No custom fields',
          description: 'Create a field definition to start configuring custom fields.',
          action: <Button label="+ New Field" onClick={openCreate} />,
        }}
      />

      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        title={editing ? `Edit Field: ${editing.label}` : 'New Custom Field'}
      >
        <ZodForm
          schema={DefinitionSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          formRef={formRef}
        >
          {editing ? (
            <FormText name="fieldKey" label="Field Key" disabled />
          ) : (
            <FormDropdown name="fieldKey" label="Field Key" options={keyOptions} required />
          )}
          <FormText name="label" label="Label" required autoFocus />
          <FormSwitch name="isRequired" label="Required" />
          <FormSwitch name="requiredOnClose" label="Required on Close" />
          <FormSwitch name="allowMultiple" label="Allow Multiple" />
          <FormNumber name="sortOrder" label="Sort order" min={0} />
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Values tab
// ---------------------------------------------------------------------------

interface ValuesTabProps {
  definitions: FieldDefinition[]
  slaPolicies: SlaPolicy[]
}

function ValuesTab({ definitions, slaPolicies }: ValuesTabProps): JSX.Element {
  const toast = useToast()
  // selectedFieldKey holds the *field definition id* (per plan §0.D snippet).
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null)
  const [values, setValues] = useState<ValueRow[]>([])
  const [valuesLoading, setValuesLoading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const [editorOpen, setEditorOpen] = useState(false)
  const [editingValue, setEditingValue] = useState<ValueRow | null>(null)
  // For "add child", the seeded parent/level for a brand-new value.
  const [seedParentId, setSeedParentId] = useState<string | null>(null)
  const [seedLevel, setSeedLevel] = useState<number>(1)

  // §0.D — raw api.get for the query-string path.
  useEffect(() => {
    if (!selectedFieldKey) {
      setValues([])
      return
    }
    let cancelled = false
    setValuesLoading(true)
    api
      .get<{ rows: ValueRow[] }>(`${VALUES_PATH}?fieldDefinitionId=${selectedFieldKey}`)
      .then((resp) => {
        if (!cancelled) setValues(resp.rows ?? [])
      })
      .catch(() => {
        if (!cancelled) {
          setValues([])
          toast.error('Failed to load values', 'Could not fetch values for this field.')
        }
      })
      .finally(() => {
        if (!cancelled) setValuesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedFieldKey, refreshKey, toast])

  const refetchValues = useCallback(() => setRefreshKey((k) => k + 1), [])

  const tree = assembleTree(
    values.map((v) => ({
      id: v.id,
      parentValueId: v.parentValueId,
      level: v.level,
      label: v.label,
    })),
  )

  function openAddChild(parentId: string | null, level: number): void {
    setEditingValue(null)
    setSeedParentId(parentId)
    setSeedLevel(level)
    setEditorOpen(true)
  }

  function openEditValue(id: string): void {
    const row = values.find((v) => v.id === id) ?? null
    setEditingValue(row)
    setSeedParentId(row?.parentValueId ?? null)
    setSeedLevel(row?.level ?? 1)
    setEditorOpen(true)
  }

  function handleDeleteValue(id: string): void {
    const row = values.find((v) => v.id === id)
    confirmDialog({
      message: `Permanently delete the "${row?.label ?? 'value'}"? Its child values will be orphaned. This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        void api.delete(`${VALUES_PATH}/${id}`).then(refetchValues)
      },
    })
  }

  async function handleSaveValue(input: ValueInput): Promise<void> {
    if (editingValue) {
      await api.patch(`${VALUES_PATH}/${editingValue.id}`, input)
    } else {
      await api.post(VALUES_PATH, { ...input, fieldDefinitionId: selectedFieldKey })
    }
    refetchValues()
  }

  // Parents available for the editor's parent dropdown. When editing a value,
  // exclude both that value AND all of its transitive descendants — otherwise
  // the user could pick one of the value's own children/grandchildren as its
  // parent, creating a cycle (the backend does not validate acyclicity).
  const excludedParentIds = editingValue
    ? new Set<string>([editingValue.id, ...collectDescendantIds(values, editingValue.id)])
    : new Set<string>()
  const availableParents = values
    .filter((v) => !excludedParentIds.has(v.id))
    .map((v) => ({ id: v.id, label: v.label, level: v.level }))

  const initialValues: Partial<ValueInput> = editingValue
    ? {
        label: editingValue.label,
        code: editingValue.code,
        color: editingValue.color ?? null,
        icon: editingValue.icon ?? null,
        parentValueId: editingValue.parentValueId,
        level: editingValue.level,
        slaOverrideId: editingValue.slaOverrideId ?? null,
        isActive: editingValue.isActive ?? true,
        sortOrder: editingValue.sortOrder ?? 100,
      }
    : { parentValueId: seedParentId, level: seedLevel }

  return (
    <div className="flex flex-col gap-4">
      <label className="flex max-w-sm flex-col gap-1 text-sm">
        <span className="font-medium">Select field</span>
                <Select
          ariaLabel="Select field"
          size="sm"
          value={selectedFieldKey ?? ''}
          onChange={(v) => setSelectedFieldKey(v || null)}
        >
          <option value="">— Select a field —</option>
          {definitions.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
            </option>
          ))}
                </Select>
      </label>

      {!selectedFieldKey && (
        <p className="text-sm text-gray-500">Select a field to view and edit its values.</p>
      )}

      {selectedFieldKey && valuesLoading && (
        <p className="text-sm text-gray-500">Loading values…</p>
      )}

      {selectedFieldKey && !valuesLoading && (
        <CustomFieldValuesTree
          values={tree}
          onEdit={openEditValue}
          onDelete={handleDeleteValue}
          onAddChild={openAddChild}
        />
      )}

      {selectedFieldKey && (
        <CustomFieldValuesEditor
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
          onSave={handleSaveValue}
          initialValues={initialValues}
          fieldDefinitionId={selectedFieldKey}
          availableParents={availableParents}
          slaPolicies={slaPolicies}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Dependencies tab
// ---------------------------------------------------------------------------

interface DependenciesTabProps {
  definitions: FieldDefinition[]
  dependencies: Dependency[]
  refetchDependencies: () => void
}

function DependenciesTab({
  definitions,
  dependencies,
  refetchDependencies,
}: DependenciesTabProps): JSX.Element {
  const toast = useToast()
  // valuesByField — fetched once per configured field when this tab mounts.
  const [valuesByField, setValuesByField] = useState<Record<string, FieldValue[]>>({})
  const [valuesLoading, setValuesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setValuesLoading(true)
    Promise.all(
      definitions.map((d) =>
        api
          .get<{ rows: ValueRow[] }>(`${VALUES_PATH}?fieldDefinitionId=${d.id}`)
          .then((resp) => [d.id, resp.rows ?? []] as const),
      ),
    )
      .then((entries) => {
        if (cancelled) return
        const next: Record<string, FieldValue[]> = {}
        for (const [fieldId, rows] of entries) {
          next[fieldId] = rows.map((r) => ({
            id: r.id,
            label: r.label,
            fieldDefinitionId: r.fieldDefinitionId,
          }))
        }
        setValuesByField(next)
      })
      .catch(() => {
        if (!cancelled) {
          setValuesByField({})
          toast.error('Failed to load values', 'Could not fetch field values for dependency rules.')
        }
      })
      .finally(() => {
        if (!cancelled) setValuesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [definitions, toast])

  async function handleCreate(input: Omit<Dependency, 'id'>): Promise<void> {
    await api.post(DEPENDENCIES_PATH, input)
    refetchDependencies()
  }

  async function handleDelete(id: string): Promise<void> {
    await api.delete(`${DEPENDENCIES_PATH}/${id}`)
    refetchDependencies()
  }

  if (valuesLoading) {
    return <p className="text-sm text-gray-500">Loading values…</p>
  }

  return (
    <CustomFieldDependenciesEditor
      rules={dependencies}
      fields={definitions.map((d) => ({ id: d.id, fieldKey: d.fieldKey, label: d.label }))}
      valuesByField={valuesByField}
      onCreate={handleCreate}
      onDelete={handleDelete}
    />
  )
}
